import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnDestroy, OnInit, Renderer2, signal } from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { FooterComponent } from '../shared/footer/footer.component';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { auditTime, bufferWhen, catchError, filter, from, map, mergeMap, of, scan, startWith, Subject, switchMap, timer, toArray } from 'rxjs';
import { LandingPageData } from '../shared/types/landing.types';
import { formatStringForURL } from '../shared/common/utils';
import { Product } from '../product/product.types';
import { ProductsService } from '../product/products.service';
import { ModalContentService } from '../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../shared/constants/modal-content-types';
import { ModalContentData } from '../shared/modal-content/modal-content';
import { formatDateToReadableString, readableDateOrEmpty } from '../shared/utils/formatting';
import { CanonicalService } from '../shared/canonical.service';
import { ResolutionService } from '../shared/resolution.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-home',
  imports: [FooterComponent, RouterLink, NgTemplateOutlet],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  host: { 'class': 'home-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, OnDestroy {

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly productsService = inject(ProductsService);
  private readonly modalService = inject(ModalContentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resolutionService = inject(ResolutionService);
  readonly #canonicalService = inject(CanonicalService);
  readonly #renderer = inject(Renderer2);
  readonly #document = inject(DOCUMENT);

  #jsonLdScript: HTMLScriptElement | null = null;

  heroText = "Colorado's largest selection of pasture raised local meats, poultry + wild caught Alaskan seafood. Since 2013.";

  private readonly emptyLanding: LandingPageData = { heroBanner: null, categories: [] };

  landing = toSignal(
    this.route.data.pipe(map(data => (data['landing'] as LandingPageData) ?? this.emptyLanding)),
    { initialValue: this.emptyLanding }
  );

  categories = computed(() => this.landing().categories);

  topCategories = computed(() => this.categories().slice(0, 2));
  customerFavoritesCategory = computed(() => this.categories()[2] ?? null);
  bottomCategories = computed(() => this.categories().slice(3));

  heroBannerSrc = computed(() => {
    const hero = this.landing().heroBanner;
    if (!hero) return null;
    return (this.resolutionService.isMobile() ? hero.mobile : hero.desktop) ?? null;
  });

  heroBannerUrl = computed(() => this.landing().heroBanner?.url ?? null);

  productDetails = signal<Map<number, Product>>(new Map());
  #requestedIds = new Set<number>();
  #visibleProducts$ = new Subject<number[]>();

  constructor() {
    this.#setupBatchRequests();
  }

  ngOnInit(): void {
    this.#canonicalService.setPageTitle(environment.config.seo.mainTitle);
    this.#canonicalService.setPageUrl(environment.config.seo.baseURL);
    this.#canonicalService.setPageType('website');
    this.#setLocalBusinessJsonLd();
  }

  ngOnDestroy(): void {
    if (this.#jsonLdScript) {
      this.#renderer.removeChild(this.#document.head, this.#jsonLdScript);
      this.#jsonLdScript = null;
    }
  }

  #setLocalBusinessJsonLd(): void {
    if (this.#jsonLdScript) {
      this.#renderer.removeChild(this.#document.head, this.#jsonLdScript);
      this.#jsonLdScript = null;
    }

    const existing = this.#document.head.querySelector('script[type="application/ld+json"]');
    if (existing?.textContent &&
      (existing.textContent.includes('"LocalBusiness"') ||
        existing.textContent.includes('"FoodEstablishment"') ||
        existing.textContent.includes('"GroceryStore"'))) {
      this.#renderer.removeChild(this.#document.head, existing);
    }

    const lb = environment.config.seo.localBusiness;
    if (!lb) return;

    const jsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': lb.type,
      'name': environment.application.name,
      'url': environment.config.seo.baseURL,
      'description': environment.config.seo.mainDescription,
      'telephone': lb.telephone,
      'image': lb.image,
      'priceRange': lb.priceRange,
      'address': {
        '@type': 'PostalAddress',
        ...lb.address
      }
    };

    if (lb.logo) jsonLd['logo'] = lb.logo;
    if (lb.sameAs?.length) jsonLd['sameAs'] = lb.sameAs;
    if (environment.config.contactEmail) {
      jsonLd['contactPoint'] = {
        '@type': 'ContactPoint',
        'contactType': 'customer service',
        'email': environment.config.contactEmail
      };
    }
    if (lb.type === 'FoodEstablishment' && lb.servesCuisine?.length) {
      jsonLd['servesCuisine'] = lb.servesCuisine;
    }
    if (lb.areaServed?.length) {
      jsonLd['areaServed'] = lb.areaServed.map(area => ({ '@type': area.type ?? 'City', 'name': area.name }));
    }
    if (lb.paymentAccepted) jsonLd['paymentAccepted'] = lb.paymentAccepted;
    if (lb.currenciesAccepted) jsonLd['currenciesAccepted'] = lb.currenciesAccepted;

    this.#jsonLdScript = this.#renderer.createElement('script');
    this.#jsonLdScript!.type = 'application/ld+json';
    this.#jsonLdScript!.text = JSON.stringify(jsonLd);
    this.#renderer.appendChild(this.#document.head, this.#jsonLdScript);
  }

  onImgError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/images/product-placeholder.webp';
  }

  navigateToCategory(name: string): void {
    this.router.navigateByUrl(this.getShopAllLink(name));
  }

  navigateToProduct(seoUrl: string, event: Event): void {
    event.stopPropagation();
    this.router.navigateByUrl(`/shop/${seoUrl}`);
  }

  getShopAllLink(name: string): string {
    return `/shop/${formatStringForURL(name)}`;
  }

  getShopAllText(name: string): string {
    return `Shop all ${name}`;
  }

  onProductVisible(productId: number): true {
    if (!this.#requestedIds.has(productId)) {
      this.#requestedIds.add(productId);
      this.#visibleProducts$.next([productId]);
    }
    return true;
  }

  getProductDetail(productId: number): Product | undefined {
    return this.productDetails().get(productId);
  }

  hasBundle(productId: number): boolean {
    const product = this.productDetails().get(productId);
    return !!(product?.bundle?.items?.length);
  }

  isOnSale(productId: number): boolean {
    const product = this.productDetails().get(productId);
    return !!(product?.hasDiscount && product?.isOnSale);
  }

  openWhatsInside(product: Product, event: Event): void {
    event.stopPropagation();
    if (!product?.bundle) return;

    // The week window lives on the published bundle; bundle.startDate/endDate come
    // back null from the API, which is what rendered as "NaN/NaN/NaN".
    const publishedBundle = product.bundle.publishedBundles?.[0];
    const startDate = readableDateOrEmpty(publishedBundle?.dateFrom ?? product.bundle.startDate);
    const endDate = readableDateOrEmpty(publishedBundle?.dateTo ?? product.bundle.endDate);

    this.modalService.openModal(ModalContentTypes.BOX_DETAIL, {
      title: product.name,
      closeable: true,
      boxDetail: {
        isSubscription: false,
        existsOneTimeInOrder: false,
        existsSubscriptionInOrder: false,
        shouldLockBundle: !!(product.bundle?.isLocked && !(product.bundle.premiumAddables?.length)),
        existsInOrder: false,
        startDate,
        endDate,
        publishedBundles: product.bundle.publishedBundles ?? [],
        detail: product.bundle.items.map(e => {
          const data = formatDateToReadableString(e.salePriceEndDate || '');
          const mmddyyyyFormat = data.mmddyyyyFormat || '';
          return {
            img: e.img,
            productName: e.name,
            quantity: e.quantity,
            packageName: e.packageName || e.display || '',
            producer: e.producerName || '',
            price: e.price,
            isOnSale: e.isOnSale || false,
            salePriceEndDate: e.salePriceEndDate || '',
            shortDiscountDisclaimer: mmddyyyyFormat ? `Deal available until ${mmddyyyyFormat}.` : '',
            originalPrice: e.originalPrice || 0,
          };
        })
      }
    } as ModalContentData);
  }

  #setupBatchRequests(): void {
    const BATCH_SIZE = 15;
    const MAX_CONCURRENT_REQUESTS = 2;

    const noActivity$ = this.#visibleProducts$.pipe(
      switchMap(() => timer(150).pipe(map(() => true))),
      startWith(false)
    );

    this.#visibleProducts$.pipe(
      bufferWhen(() => noActivity$.pipe(filter(inactive => inactive))),
      filter(productGroups => productGroups.length > 0),
      mergeMap(productGroups => productGroups.flat()),
      scan((acc: number[], productId: number) => [...new Set([...acc, productId])], []),
      auditTime(400),
      switchMap(productIds => {
        const idsToFetch = productIds.filter(id => !this.productDetails().has(id) && this.#requestedIds.has(id));
        if (idsToFetch.length === 0) return of([]);

        const chunks: number[][] = [];
        for (let i = 0; i < idsToFetch.length; i += BATCH_SIZE) {
          chunks.push(idsToFetch.slice(i, i + BATCH_SIZE));
        }
        chunks.reverse();

        return from(chunks).pipe(
          mergeMap(
            chunk => this.productsService.getProductsDetailsByIds(chunk).pipe(
              catchError(() => of({}))
            ),
            MAX_CONCURRENT_REQUESTS
          ),
          toArray()
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(responses => {
      this.productDetails.update(prev => {
        const updatedMap = new Map(prev);
        responses.forEach(details => {
          Object.entries(details).forEach(([, detail]) => {
            const productDetail = detail as Product;
            updatedMap.set(Number(productDetail.id), productDetail);
          });
        });
        return updatedMap;
      });
    });
  }
}
